import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { ChatActionType, type RepSearchResult } from '@trinity/chat';
import { SelectRep } from '@trinity/chat-components/components/Search/Rep';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { SearchQueryHint } from '../presentation/Message.QueryHint';
import { SubmitActionContext } from './context';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  disabled?: boolean;
  searchRepsQueryFn?: (params: {
    name: string;
    brandIdentifier: string | null;
  }) => Promise<RepSearchResult[]>;
};

export const RepSearchResponse = ({ disabled = false, searchRepsQueryFn }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const apiClient = useChatApiClient();

  const [inputValue, setInputValue] = useState('');
  const [selectedRep, setSelectedRep] = useState<RepSearchResult | null>(null);

  const queryFn = useCallback(
    async ({ name, brandIdentifier }: { name: string; brandIdentifier: string | null }) => {
      if (searchRepsQueryFn) {
        return searchRepsQueryFn({ name, brandIdentifier });
      }

      const res = await apiClient.search.searchReps({
        name,
        brandIdentifier: brandIdentifier ?? '',
      });

      return res.results;
    },
    [apiClient, searchRepsQueryFn]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setSelectedRep(null);
  }, []);

  const handleSelect = useCallback(
    (rep: RepSearchResult) => {
      if (disabled) return;

      setSelectedRep(rep);
      setInputValue(rep.name);

      submitAction(
        {
          type: ChatActionType.SelectRepSubject,
          payload: {
            repId: rep.id,
            repName: rep.name,
          },
        },
        `Show me the summary for ${rep.name}.`
      );
    },
    [disabled, submitAction]
  );

  const handleSubmit = useCallback(() => {
    if (disabled) return;

    if (selectedRep) {
      submitAction(
        {
          type: ChatActionType.SelectRepSubject,
          payload: {
            repId: selectedRep.id,
            repName: selectedRep.name,
          },
        },
        `Show me the summary for ${selectedRep.name}.`
      );
      return;
    }

    const trimmedName = inputValue.trim();
    if (trimmedName.length === 0) return;

    submitAction(
      {
        type: ChatActionType.SearchReps,
        payload: { name: trimmedName },
      },
      `Find any matching Rep with the name ${trimmedName}`
    );
  }, [disabled, inputValue, selectedRep, submitAction]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          <IntroText>
            Get started with a search to pull a Rep Profile
            <br />
            First let&apos;s find the Rep you&apos;re looking for.
          </IntroText>

          <FormRow>
            <Label>Name</Label>
            <SelectRep
              disabled={disabled}
              inputValue={inputValue}
              selectedRep={selectedRep}
              onInputChange={handleInputChange}
              onSelection={handleSelect}
              onKeyDown={handleKeyDown}
              queryFn={queryFn}
            />
          </FormRow>

          <SearchQueryHint
            disabled={disabled || inputValue.trim().length === 0}
            onClick={handleSubmit}
            value="Search for profiles."
          />
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

const Body = styled.div({
  width: '100%',
});

const IntroText = styled.div(({ theme }) => ({
  marginBottom: 16,
  fontFamily: theme.fonts.regular,
}));

const FormRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 16,
});

const Label = styled.span(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  minWidth: 50,
}));
